const MAIN_FORM_DRAFT_KEY = 'spotlight_main_form_draft';
/** Empty-option label when subject is disabled until level is chosen (must match form-main.php). */
const SUBJECT_PLACEHOLDER_DISABLED = 'Select a level first';
/** Empty-option label when level is set and user should pick a subject. */
const SUBJECT_PLACEHOLDER_ENABLED = '-- Select one --';

export default class MainForm {
	constructor(form) {
		this.form = form;
		this.subjectOptions = this.getSubjectOptions();
		this.levelSelect = this.form.querySelector('[name="level"]');
		this.subjectSelect = this.form.querySelector('[name="subject"]');
		this.postcodeField = this.form.querySelector('[name="postcode"]');
		this._postcodeAbortController = null;
		this._postcodeDebounceTimer = null;
		this._sessionDraftSaveTimer = null;

		const draftRestored = this.restoreFormDraftFromSession();

		this.initFilledClassToggles();
		this.initPostcodeCircleLookup();
		this.initSessionDraftPersistence();

		if (this.subjectOptions && this.levelSelect && this.subjectSelect) {
			this.levelSelect.addEventListener('change', () => this.setSubjectOptionsFromLevel());
			// After a draft restore with a level, options were rebuilt inside restore.
			if (!draftRestored || !this.levelSelect.value) {
				this.setSubjectOptionsFromLevel(); // initial sync (e.g. level already selected)
			}
		}
	}

	/**
	 * Serialize user-visible fields to sessionStorage (excludes hidden and buttons).
	 */
	saveFormDraftToSession() {
		const payload = {};
		const fields = this.form.querySelectorAll('input[name], select[name], textarea[name]');

		fields.forEach((field) => {
			const name = field.getAttribute('name');
			if (!name) return;

			if (field instanceof HTMLInputElement) {
				const type = (field.type || '').toLowerCase();
				if (type === 'hidden' || type === 'button' || type === 'submit' || type === 'reset' || type === 'file') {
					return;
				}
				if (type === 'checkbox') {
					if (!Array.isArray(payload[name])) payload[name] = [];
					if (field.checked) payload[name].push(field.value);
					return;
				}
				if (type === 'radio') {
					if (field.checked) payload[name] = field.value;
					return;
				}
			}

			if ('value' in field) {
				payload[name] = field.value;
			}
		});

		try {
			sessionStorage.setItem(MAIN_FORM_DRAFT_KEY, JSON.stringify(payload));
		} catch {
			// ignore quota / private mode
		}
	}

	/**
	 * @param {string} name
	 * @param {unknown} value
	 */
	restoreField(name, value) {
		if (value === undefined || value === null) return;

		const el = this.form.elements.namedItem(name);
		if (!el) return;

		if (el instanceof RadioNodeList) {
			if (Array.isArray(value)) {
				for (const node of el) {
					if (node instanceof HTMLInputElement && node.type === 'checkbox') {
						node.checked = value.includes(node.value);
					}
				}
			} else {
				const str = String(value);
				for (const node of el) {
					if (node instanceof HTMLInputElement && node.type === 'radio') {
						node.checked = node.value === str;
					}
				}
			}
			return;
		}

		if (el instanceof HTMLInputElement && el.type === 'checkbox') {
			el.checked = Array.isArray(value) ? value.includes(el.value) : Boolean(value);
			return;
		}

		if ('value' in el) {
			el.value = String(value);
		}
	}

	/**
	 * Restore fields from sessionStorage (level before subject so options exist).
	 * @returns {boolean} whether a non-empty draft was applied
	 */
	restoreFormDraftFromSession() {
		let data;
		try {
			const raw = sessionStorage.getItem(MAIN_FORM_DRAFT_KEY);
			if (!raw) return false;
			data = JSON.parse(raw);
		} catch {
			return false;
		}

		if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
		const keys = Object.keys(data);
		if (!keys.length) return false;

		if (typeof data.level === 'string' && data.level && this.levelSelect) {
			this.levelSelect.value = data.level;
			if (this.subjectOptions && this.subjectSelect) {
				this.setSubjectOptionsFromLevel();
			}
		}

		for (const name of keys) {
			if (name === 'level') continue;
			this.restoreField(name, data[name]);
		}

		return true;
	}

	/**
	 * Persist draft on input (debounced) and change (immediate).
	 */
	initSessionDraftPersistence() {
		const scheduleSave = () => {
			if (this._sessionDraftSaveTimer) window.clearTimeout(this._sessionDraftSaveTimer);
			this._sessionDraftSaveTimer = window.setTimeout(() => {
				this._sessionDraftSaveTimer = null;
				this.saveFormDraftToSession();
			}, 300);
		};

		this.form.addEventListener('input', scheduleSave);
		this.form.addEventListener('change', () => {
			if (this._sessionDraftSaveTimer) window.clearTimeout(this._sessionDraftSaveTimer);
			this._sessionDraftSaveTimer = null;
			this.saveFormDraftToSession();
		});
		// Flush before AJAX handling so the latest values (including inside the debounce window) are stored
		// before redirect; helps when users return via back link or browser back.
		this.form.addEventListener('submit', () => {
			if (this._sessionDraftSaveTimer) window.clearTimeout(this._sessionDraftSaveTimer);
			this._sessionDraftSaveTimer = null;
			this.saveFormDraftToSession();
		});
	}

	/**
	 * Toggle `.filled` on the closest `.field-wrapper` for a field.
	 * A field is considered "filled" when it has a non-empty value (or is checked, for checkboxes/radios).
	 * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} field
	 * @returns {boolean} whether the field is considered filled
	 */
	syncFilledClass(field) {
		const wrapper = field?.closest?.('.field-wrapper');
		if (!wrapper) return false;

		let isFilled = false;

		if (field instanceof HTMLInputElement) {
			const type = (field.getAttribute('type') || '').toLowerCase();
			if (type === 'checkbox' || type === 'radio') {
				isFilled = field.checked;
			} else if (type === 'file') {
				isFilled = (field.files?.length || 0) > 0;
			} else {
				isFilled = field.value.trim().length > 0;
			}
		} else if (field instanceof HTMLSelectElement) {
			isFilled = field.value.trim().length > 0;
		} else if (field instanceof HTMLTextAreaElement) {
			isFilled = field.value.trim().length > 0;
		}

		wrapper.classList.toggle('filled', isFilled);
		return isFilled;
	}

	/**
	 * Toggle `.active` on any circle(s) related to this field via `data-field="<field name>"`.
	 * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} field
	 * @param {boolean} isActive
	 */
	syncCircleActiveClass(field, isActive) {
		const name = field?.getAttribute?.('name') || field?.name;
		if (!name) return;

		const escaped = (globalThis.CSS && typeof globalThis.CSS.escape === 'function')
			? globalThis.CSS.escape(name)
			: name.replace(/"/g, '\\"');

		const circles = this.form.querySelectorAll(`.circle[data-field="${escaped}"]`);
		circles.forEach((circle) => circle.classList.toggle('active', Boolean(isActive)));
	}

	/**
	 * Populate `.circle-text` for the circle(s) related to this field.
	 * For selects, prefers the selected option's `data-circle-text`.
	 * For text inputs (e.g. postcode), falls back to a simple derived label.
	 * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} field
	 * @param {boolean} isActive
	 */
	syncCircleText(field, isActive) {
		const name = field?.getAttribute?.('name') || field?.name;
		if (!name) return;

		const escaped = (globalThis.CSS && typeof globalThis.CSS.escape === 'function')
			? globalThis.CSS.escape(name)
			: name.replace(/"/g, '\\"');

		const circles = this.form.querySelectorAll(`.circle[data-field="${escaped}"]`);
		if (!circles.length) return;

		let text = '';

		if (isActive) {
			if (field instanceof HTMLSelectElement) {
				const opt = field.selectedOptions?.[0];
				text = opt?.getAttribute?.('data-circle-text') || opt?.textContent?.trim() || '';
			} else if (field instanceof HTMLInputElement) {
				const type = (field.getAttribute('type') || '').toLowerCase();
				if (type === 'checkbox' || type === 'radio') {
					text = field.checked ? (field.value || 'Selected') : '';
				} else {
					const val = field.value.trim();
					if (name === 'postcode') {
						text = `I'm based in ${val.toUpperCase()}`;
					} else {
						text = val;
					}
				}
			} else if (field instanceof HTMLTextAreaElement) {
				text = field.value.trim();
			}
		}

		circles.forEach((circle) => {
			const el = circle.querySelector('.circle-text');
			if (!el) return;
			el.textContent = text;
		});
	}

	normalizePostcode(raw) {
		return String(raw || '').trim().toUpperCase().replace(/\s+/g, '');
	}

	getPostcodeFallbackPlace(postcode) {
		// UK outward code (variable length), e.g. "BN25 9AA" -> "BN25", "SW1A 1AA" -> "SW1A"
		// If it doesn't match, fall back to first 4 chars of the normalized string.
		const normalized = this.normalizePostcode(postcode);
		const match = normalized.match(/^[A-Z]{1,2}\d[A-Z\d]?/);
		return (match?.[0] || normalized.slice(0, Math.min(4, normalized.length))).trim();
	}

	async fetchPostcodePlace(postcode) {
		// `params` is defined in `includes/meta.php` as a global `const params = {...}`.
		// In browsers, global `const`/`let` bindings are NOT attached to `window`, so prefer
		// reading the identifier directly when available.
		// eslint-disable-next-line no-undef
		const url = (typeof params !== 'undefined' && params && params.spotlight_postcode_url)
			? params.spotlight_postcode_url
			: (typeof window !== 'undefined' && window.params ? window.params.spotlight_postcode_url : null);

		// Optional debugging: set `window.__spotlightPostcodeDebug = true`
		const debug = typeof window !== 'undefined' && Boolean(window.__spotlightPostcodeDebug);
		if (debug) console.log('[spotlight] postcode endpoint url', url);

		if (!url) return null;

		// session cache (client-side) to avoid repeated calls during a visit
		const cacheKey = `spotlight_postcode_place:${postcode}`;
		try {
			const cached = sessionStorage.getItem(cacheKey);
			if (cached) {
				if (debug) console.log('[spotlight] postcode cache hit', { cacheKey, cached });
				return cached;
			}
		} catch {
			// ignore storage failures
		}

		// Abort any in-flight request
		if (this._postcodeAbortController) {
			this._postcodeAbortController.abort();
		}
		this._postcodeAbortController = new AbortController();

		const requestUrl = `${url}?postcode=${encodeURIComponent(postcode)}`;
		if (typeof window !== 'undefined') {
			window.__spotlightLastPostcodeLookup = { postcode, requestUrl, at: Date.now() };
		}
		if (debug) console.log('[spotlight] postcode lookup →', requestUrl);

		let res;
		try {
			res = await fetch(requestUrl, {
				method: 'GET',
				headers: { 'Accept': 'application/json' },
				signal: this._postcodeAbortController.signal
			});
		} catch (err) {
			if (debug) console.log('[spotlight] postcode lookup failed (network/CORS)', err);
			return null;
		}

		if (debug) console.log('[spotlight] postcode lookup status', res.status);
		if (!res.ok) return null;

		let data = null;
		try {
			data = await res.json();
		} catch {
			return null;
		}

		const place =
			(data && data.success === true && typeof data.place === 'string')
				? data.place.trim()
				: null;
		if (!place) return null;

		try {
			sessionStorage.setItem(cacheKey, place);
		} catch {
			// ignore storage failures
		}

		return place;
	}

	setCircleTextByFieldName(fieldName, text) {
		if (!fieldName) return;
		const escaped = (globalThis.CSS && typeof globalThis.CSS.escape === 'function')
			? globalThis.CSS.escape(fieldName)
			: String(fieldName).replace(/"/g, '\\"');

		const circles = this.form.querySelectorAll(`.circle[data-field="${escaped}"]`);
		circles.forEach((circle) => {
			const el = circle.querySelector('.circle-text');
			if (!el) return;
			el.textContent = text || '';
		});
	}

	initPostcodeCircleLookup() {
		if (!this.postcodeField) return;

		const run = async () => {
			const debug = typeof window !== 'undefined' && Boolean(window.__spotlightPostcodeDebug);
			const postcode = this.normalizePostcode(this.postcodeField.value);
			const isFilled = postcode.length > 0;
			if (debug) console.log('[spotlight] postcode run', { postcode, isFilled });

			// Always keep the field-wrapper `.filled` state in sync
			this.syncFilledClass(this.postcodeField);

			// Keep the circle inactive until we have a result to show.
			if (!isFilled) {
				this.syncCircleActiveClass(this.postcodeField, false);
				this.setCircleTextByFieldName('postcode', '');
				return;
			}

			// Only attempt lookup (and only show outward-code fallback) when we have a full-looking postcode.
			// This prevents premature fallback while the user is still typing.
			if (postcode.length < 5) {
				this.syncCircleActiveClass(this.postcodeField, false);
				this.setCircleTextByFieldName('postcode', '');
				return;
			}

			const place = await this.fetchPostcodePlace(postcode);
			const finalPlace = place || this.getPostcodeFallbackPlace(postcode);
			this.setCircleTextByFieldName('postcode', `I'm based in ${finalPlace}`);
			this.syncCircleActiveClass(this.postcodeField, true);
		};

		const schedule = () => {
			if (this._postcodeDebounceTimer) window.clearTimeout(this._postcodeDebounceTimer);
			this._postcodeDebounceTimer = window.setTimeout(run, 600);
		};

		// Debounced while typing; immediate on blur/change
		this.postcodeField.addEventListener('input', () => {
			// While typing, keep circle inactive/blank
			this.syncFilledClass(this.postcodeField);
			this.syncCircleActiveClass(this.postcodeField, false);
			this.setCircleTextByFieldName('postcode', '');
			schedule();
		});
		this.postcodeField.addEventListener('change', run);
		// Note: `change` fires on blur when the value changed, so we don't also bind `blur`,
		// otherwise the lookup runs twice.

		// Initialize
		run();
	}

	/**
	 * Add listeners to keep `.field-wrapper.filled` in sync with field values.
	 */
	initFilledClassToggles() {
		const fields = Array.from(this.form.querySelectorAll('input, select, textarea'));

		fields.forEach((field) => {
			// Skip non-value controls
			if (field instanceof HTMLInputElement) {
				const type = (field.getAttribute('type') || '').toLowerCase();
				if (type === 'button' || type === 'submit' || type === 'reset' || type === 'hidden') return;
			}

			// Postcode is handled separately (async lookup + delayed activation)
			const fieldName = field?.getAttribute?.('name') || field?.name;
			if (fieldName === 'postcode') return;

			const handler = () => {
				const isFilled = this.syncFilledClass(field);
				this.syncCircleActiveClass(field, isFilled);
				this.syncCircleText(field, isFilled);
			};

			// Initialize on load (e.g. browser autofill, server-rendered values)
			handler();

			if (field instanceof HTMLSelectElement) {
				field.addEventListener('change', handler);
			} else {
				field.addEventListener('input', handler);
				field.addEventListener('change', handler);
			}
		});
	}

	/**
	 * Read undergraduate/postgraduate subject options from the JSON script in the form.
	 * @returns {{ undergraduate: Array<{name: string, val: string}>, postgraduate: Array<{name: string, val: string}> }|null}
	 */
	getSubjectOptions() {
		const script = this.form.querySelector('#main-form-subject-options');
		if (!script || !script.textContent) return null;
		try {
			return JSON.parse(script.textContent.trim());
		} catch {
			return null;
		}
	}

	/**
	 * Set the subject select options based on the current level selection.
	 * Enables the subject field when a level is chosen; clears and disables it when level is empty.
	 */
	setSubjectOptionsFromLevel() {
		const level = this.levelSelect.value;

		// Clear existing options except we'll add placeholder back
		this.subjectSelect.innerHTML = '';
		this.subjectSelect.value = '';

		if (!level || !this.subjectOptions[level]) {
			this.subjectSelect.setAttribute('disabled', '');
			const placeholder = document.createElement('option');
			placeholder.value = '';
			placeholder.textContent = SUBJECT_PLACEHOLDER_DISABLED;
			this.subjectSelect.appendChild(placeholder);
			const isFilled = this.syncFilledClass(this.subjectSelect);
			this.syncCircleActiveClass(this.subjectSelect, isFilled);
			return;
		}

		const options = this.subjectOptions[level];
		const placeholder = document.createElement('option');
		placeholder.value = '';
		placeholder.textContent = SUBJECT_PLACEHOLDER_ENABLED;
		this.subjectSelect.appendChild(placeholder);

		options.forEach(({ name, val, ['circle-text']: circleText }) => {
			const option = document.createElement('option');
			option.value = val;
			option.textContent = name;
			if (typeof option.dataset !== 'undefined' && circleText) option.dataset.circleText = circleText;
			this.subjectSelect.appendChild(option);
		});

		this.subjectSelect.removeAttribute('disabled');
		const isFilled = this.syncFilledClass(this.subjectSelect);
		this.syncCircleActiveClass(this.subjectSelect, isFilled);
		this.syncCircleText(this.subjectSelect, isFilled);
	}
}